import { Box, Grid } from '@mui/material'
import { Config } from 'config'
import { KEY_STORAGE, removeBranchStorage } from 'constants/common'
import { loginFailed, loginSuccess } from 'context/login-context'
import { useCallback, useEffect, useState } from 'react'
import Cookies from 'universal-cookie'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** CONSTANTS */
import { cliniterWatermark, templateImage, watermark } from 'constants/theme'

/** API */
import LoginApi from 'api/login-api'
import { routeName } from 'routes/routes-name'

/** COMPONENT */
import ButtonCustom from 'component/Button/ButtonCustom'
import InputCheckbox from 'component/Input/InputCheckbox'
import InputPasswordField from 'component/Input/InputPasswordField'
import InputTextField from 'component/Input/InputTextField'
import ForgetPassword from 'features/auth/forget-password'
import { setShowAnnouncementsOnLogin } from 'features/setting/announcement/announcement.utils'

/** STYLE */
import { swalCustom } from 'component/Alert/Swal'
import useStyles, { ButtonForgetPassword, CardFormLogin, CardLogin, CliniterWatermark, License, Logo, SectionWatermark, Watermark } from 'features/auth/style'
import { getBranch, getPermissionMenus, getPermissionShortcuts, getServicePoint, getToken, setSecureItem } from 'utils/app.utils'

const initStateErrorMessage = {
  USERNAME: '',
  PASSWORD: '',
  INVALID_USERNAME_PASSWORD: ``,
  INVALID_USERNAME: ``,
  INVALID_PASSWORD: ``,
  INVALID_EMAIL: ``
}

export default function Login() {
  const { t } = useTranslation()
  const cookies = new Cookies()

  const classes = useStyles()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')

  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)
  const [loading, setLoading] = useState(false)
  const [forgetPassword, setForgetPassword] = useState(false)
  const [isSucceeded, setSucceeded] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const loadSignedIn = useCallback(async () => {
    const data = await LoginApi.rememberMe(cookies.get('TOKEN'))
    if (data) {
      setSecureItem(KEY_STORAGE.TOKEN, cookies.get('TOKEN'))
      setSecureItem('BRANCH_ID', String(data.branchId))
      setSecureItem('SERVICE_POINT_ID', String(data.servicePointId))
      window.location.href = '/'
    }
  }, [])

  useEffect(() => {
    if (cookies.get('REMEMBER_ME') && cookies.get('REMEMBER_ME') !== '') {
      setRememberMe(localStorage.rememberMe === 'true' ? true : false)
      loadSignedIn()
    }
  }, [])

  useEffect(() => {
    const currPath = window.location.href
    const token = getToken()
    const branchId = getBranch()
    const servicePointId = getServicePoint()
    const permissionMenus = getPermissionMenus()
    const permissionShortcuts = getPermissionShortcuts()

    if (token && (branchId === null || servicePointId === null || !permissionMenus || !permissionShortcuts) && !currPath.includes('pick')) {
      removeBranchStorage()
      window.location.href = routeName.pick
    }
  }, [])

  const handleChangeRememberMe = (event: any) => {
    if (event.target.checked) cookies.set('REMEMBER_ME', event.target.checked, { path: '/' })
    else cookies.remove('REMEMBER_ME')
    setRememberMe(event.target.checked)
  }

  const submit = () => {
    if (!username) return setErrorMessage({ ...errorMessage, ...{ USERNAME: t('LOGIN.MESSAGE.USERNAME') } })
    if (!password) return setErrorMessage({ ...errorMessage, ...{ PASSWORD: t('LOGIN.MESSAGE.PASSWORD') } })
    setLoading(true)
    LoginApi.login(username, password)
      .then(({ accessToken, refreshToken, tokenExpiresAt, refreshTokenExpiresAt }) => {
        loginSuccess(accessToken, rememberMe, refreshToken, tokenExpiresAt, refreshTokenExpiresAt)
        if (rememberMe) cookies.set('TOKEN', accessToken, { path: '/' })
        setShowAnnouncementsOnLogin()
        window.location.href = routeName.pick
      })
      .catch((e) => {
        const err = e.response.data
        let newMsg: any = {}
        if (err?.message?.includes(',')) {
          newMsg = err?.message.split(',').reduce((acc: any, pair: any) => {
            const [key, value] = pair.split(':');
            acc[key] = value.trim();
            return acc;
          }, {})
        } else {
          newMsg = err
        }

        loginFailed()
        setErrorMessage({ ...errorMessage, ...{ [newMsg?.message]: t(`LOGIN.MESSAGE.${newMsg?.message}`) } })

        if (newMsg?.count && newMsg?.count > 4) {
          swalCustom(t('ติดต่อเจ้าหน้าที่'),
            `<div>
        <div>${t('คุณใส่รหัสผิดเกิน {{count}} ครั้ง', { count: newMsg?.count })}</div>
        <div>${t('กรุณาทำการติดต่อผู้ดูแลระบบ เพื่อทำการเปิดการเข้าสู่ระบบ')}</div>
        </div>`,
            () => null,
            'warning',
            { cancelButton: { cancelButtonText: t('BUTTON.ENTER') }, confirmButton: { showConfirmButton: false }, showCloseButton: false }
          )
        }

      })
      .finally(() => {
        setLoading(false)
      })
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const onClickForgetPassword = () => {
    setForgetPassword((forgetPasswordStatus) => !forgetPasswordStatus)
    setSucceeded(false)
    setEmail('')
    clearErrorMessage()
  }

  const submitForgetPassword = () => {
    if (!email) return setErrorMessage({ ...errorMessage, ...{ INVALID_EMAIL: t(`LOGIN.MESSAGE.INVALID_EMAIL`) } })
    setLoading(true)

    LoginApi.forgetPassword(email)
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          setSucceeded(true)
          setEmail('')
        }
        else setErrorMessage({ ...errorMessage, ...{ INVALID_EMAIL: t(`LOGIN.MESSAGE.INVALID_EMAIL`) } })
      })
      .catch((e) => {
        const err = e.response.data
        setErrorMessage({
          ...errorMessage,
          ...{
            [err.message]: t('LOGIN.MESSAGE.INVALID_EMAIL_ACCOUNT')
          }
        })
      })
      .finally(() => setLoading(false))
  }

  return (
    <CardLogin>
      <Grid className={'h-100'} sx={{ height: '100vh !important' }}>
        <Grid item className={classes.login} >
          <CardFormLogin xs={9} xl={3} md={5} sm={8}>
            <Grid container>
              <Grid item xs={12} sm={12} xl={12} md={12} className="w-100">
                <Logo>
                  <div className='logo-wrap'>
                    <img src={Config.REACT_APP_LOGO || templateImage.logoHorizontal} alt="Logo" />
                  </div>
                </Logo>
              </Grid>
              <Grid item xs={12} sm={12} xl={12} md={12} className="w-100 mt-3 mx-3 mb-0 m-md-0">
                {!forgetPassword && (
                  <Box className={classes.card} sx={{ display: 'flex', flexDirection: 'column', backgroundColor: '#fff', border: '1px solid rgba(0,0,0,.125)', borderRadius: '0.25rem', boxShadow: '0 .5rem 1rem rgba(0,0,0,.15)' }}>
                    <Box sx={{ flex: '1 1 auto', p: '16px' }}>
                      <Box className={`${classes.loginText} login-text`} sx={{ mb: '8px' }}>{t(`LOGIN.TITLE`)}</Box>
                      <Box sx={{ flex: '1 1 auto', p: '16px' }}>
                        <Grid container spacing={2} className=" justify-content-center align-content-center">
                          <Grid item xs={12} sm={12} xl={12} md={12} className="w-100">
                            <InputTextField
                              onchange={(event) => {
                                setUsername(event.target.value)
                                clearErrorMessage()
                              }}
                              onkeyup={(event) => event.key === 'Enter' && submit()}
                              label={t(`LOGIN.INPUT.USERNAME`)}
                              helperText={errorMessage.USERNAME || errorMessage.INVALID_USERNAME}
                            />
                          </Grid>
                          <Grid item xs={12} sm={12} xl={12} md={12} className="w-100">
                            <InputPasswordField
                              onchange={(event) => {
                                setPassword(event.target.value)
                                clearErrorMessage()
                              }}
                              onkeyup={(event) => event.key === 'Enter' && submit()}
                              label={t(`LOGIN.INPUT.PASSWORD`)}
                              helperText={errorMessage.PASSWORD || errorMessage.INVALID_PASSWORD}
                            />
                          </Grid>
                        </Grid>
                        <Grid container spacing={2} className="pb-2 pt-2 justify-content-between">
                          <Grid item xl={8} md={7} className="text-sm-left text-md-left text-nowrap">
                            <InputCheckbox id="rememberMe" checked={rememberMe} onChange={handleChangeRememberMe} className={classes.inputCheckBox} label={t(`LOGIN.INPUT.REMEMBER_ME`)} />
                          </Grid>
                          <Grid item xl={4} md={5} className="text-sm-right text-md-right text-nowrap">
                            <ButtonForgetPassword className="ml-auto justify-content-end" href="#" onClick={() => onClickForgetPassword()} style={{ textDecoration: 'underline' }}>
                              {t(`LOGIN.INPUT.FORGET_PASSWORD`)}
                            </ButtonForgetPassword>
                          </Grid>
                        </Grid>

                        <Grid container>
                          <Grid item xl={8} md sm xs={12} className="text-sm-left text-md-left text-nowrap"></Grid>
                          <Grid item xl={4} md sm xs={12} className="text-sm-right text-md-right text-nowrap"></Grid>
                        </Grid>
                        <Grid item className="pb-3">
                          <ButtonCustom
                            type="submit"
                            onClick={submit}
                            textButton={t('LOGIN.BUTTON.SUBMIT')}
                            disabled={loading}
                            btnStyle={{ fontSize: '16px', marginTop: '15px', width: '100%' }} />
                        </Grid>
                      </Box>
                    </Box>
                  </Box>
                )}
                {forgetPassword && (
                  <ForgetPassword
                    onClick={onClickForgetPassword}
                    onChange={(event) => {
                      setEmail(event.target.value)
                      clearErrorMessage()
                    }}
                    submit={submitForgetPassword}
                    helperText={errorMessage.INVALID_EMAIL}
                    isSucceeded={isSucceeded}
                    loading={loading}
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={12} xl={12} md={12} className="w-100">
                <License className={`${classes.license}`}>Copyright © 2020. Cliniter | All rights reserved.</License>
                <div style={{ fontSize: '11px', color: '#888', marginTop: 4 }}>v{Config.REACT_APP_VERSION}</div>
              </Grid>
            </Grid>
          </CardFormLogin>
        </Grid>
      </Grid>
      <SectionWatermark>
        <CliniterWatermark src={cliniterWatermark} alt="waterMark" />
        <Watermark src={watermark} alt="waterMark" />
      </SectionWatermark>
    </CardLogin>
  )
}
